<template>
  <div>
    <breadcrumbs :breadcrumb-items="breadcrumbItems" />
    <UserToolbar
      v-if="account && Object.keys(account).length !== 0"
      :account="account"
    />
    <b-card>
      <CampaignToolbar
        :campaign="campaign"
        closing
      />
    </b-card>
    <CampaignNavigation />
    <b-card class="card-statistics">
      <div class="card-statistics-tools mb-2">
        <div class="head-card">
          <div class="head-card-top">
            <h4 style="margin: 0;">
              <a
                href="#"
                @click.prevent="goToSources"
              >DSP Sources </a> &gt; {{ getPartnerName($route.params.partnerId) || $route.params.partnerId }}
            </h4>
          </div>
          <div class="mt-2">
            <StatsDateRangeFilter
              :value="rangePicker"
              :is-loading="loading"
              @input="toggleRange"
              @refresh="refreshData"
            />
          </div>
        </div>
      </div>
      <div
        v-if="loading"
        class="d-flex justify-content-center mb-1"
      >
        <b-spinner label="Loading..." />
      </div>
      <b-tabs
        v-else
        v-model="activeTab"
        class="mb-2"
        @input="onTabChange"
      >
        <b-tab :title="$t('rtbTabs.byDate')">
          <div
            v-if="!statsByDate.length"
            class="text-center text-muted my-3"
          >
            {{ $t('No data for this period') }}
          </div>
          <b-table
            v-else
            :items="statsByDate"
            :fields="dateColumns"
            responsive
            striped
            foot-clone
            small
            class="text-center"
          >
            <template #head(date)>
              {{ $t('stats.date') }}
            </template>
            <template #head(impressions)>
              {{ $t('stats.impressions') }}
            </template>
            <template #head(clicks)>
              {{ $t('stats.clicks') }}
            </template>
            <template #head(ctr)>
              CTR
            </template>
            <template #head(revenue)>
              {{ $t('stats.revenue') }}
            </template>
            <template #head(spent)>
              {{ $t('stats.spent') }}
            </template>
            <template #cell(date)="data">
              {{ data.value ? getFormattedDate(new Date(data.value)) : '' }}
            </template>
            <template #cell(impressions)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(clicks)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(ctr)="data">
              {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
            </template>
            <template #cell(revenue)="data">
              {{ data.value ? currency('USD', data.value) : 0 }}
            </template>
            <template #cell(spent)="data">
              {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
            </template>
            <template #foot(date)="data">
              <strong :data-label="data.label">{{ $t('Records') }}: {{ statsByDate.length }}</strong>
            </template>
            <template #foot(impressions)="data">
              <strong :data-label="data.label">{{ totalFormat(statsByDate, 'impressions').toLocaleString('ru-RU') }}</strong>
            </template>
            <template #foot(clicks)="data">
              <strong :data-label="data.label">{{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(totalFormat(statsByDate, 'clicks')) }}</strong>
            </template>
            <template #foot(ctr)="data">
              <strong :data-label="data.label">
                {{ totalFormat(statsByDate, 'clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat(statsByDate, 'clicks') / totalFormat(statsByDate, 'impressions') * 100) : 0 }}%
              </strong>
            </template>
            <template #foot(revenue)="data">
              <strong :data-label="data.label">{{ currency('USD', totalAmount(statsByDate)) }}</strong>
            </template>
            <template #foot(spent)="data">
              <strong :data-label="data.label">{{ currency(campaign.currency || 'USD', totalFormat(statsByDate, 'spent')) }}</strong>
            </template>
          </b-table>
        </b-tab>
        <b-tab :title="$t('rtbTabs.bySource')">
          <div
            v-if="!statsBySite.length"
            class="text-center text-muted my-3"
          >
            {{ $t('No data for this period') }}
          </div>
          <b-table
            v-else
            :items="statsBySite"
            :fields="siteColumns"
            responsive
            striped
            foot-clone
            small
            :sort-by.sync="siteSortBy"
            :sort-desc.sync="siteSortDesc"
            :sort-direction="siteSortDirection"
            class="text-center"
            @sort-changed="onSiteSortChanged"
          >
            <template #head(site_id)>
              {{ $t('stats.source') }}
            </template>
            <template #head(site_id_raw)>
              ID
            </template>
            <template #head(impressions)>
              {{ $t('stats.impressions') }}
            </template>
            <template #head(clicks)>
              {{ $t('stats.clicks') }}
            </template>
            <template #head(ctr)>
              CTR
            </template>
            <template #head(revenue)>
              {{ $t('stats.revenue') }}
            </template>
            <template #head(spent)>
              {{ $t('stats.spent') }}
            </template>
            <template #cell(site_id)="data">
              <div class="d-flex align-items-center justify-content-center position-relative">
                <b-button
                  variant="link"
                  class="p-0 text-decoration-none"
                  @click="goToSourceDetails(data.value)"
                >
                  {{ getDomainNameById(data.value) || data.value }}
                </b-button>
                <feather-icon
                  v-if="getDomainNameById(data.value)"
                  icon="ExternalLinkIcon"
                  size="14"
                  class="ml-25 cursor-pointer text-primary"
                  @click.stop="openWebsite(getDomainNameById(data.value))"
                />
              </div>
            </template>
            <template #cell(site_id_raw)="{ item }">
              <span
                class="cursor-pointer copy-id"
                @click="copyToClipboard(item.site_id)"
              >
                {{ item.site_id }}
              </span>
            </template>
            <template #cell(impressions)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(clicks)="data">
              {{ data.value ? new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(data.value) : 0 }}
            </template>
            <template #cell(ctr)="data">
              {{ data.item.clicks ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(data.item.clicks / data.item.impressions * 100) + '%' : 0 }}
            </template>
            <template #cell(revenue)="data">
              {{ data.value ? currency('USD', data.value) : 0 }}
            </template>
            <template #cell(spent)="data">
              {{ data.value ? currency(campaign.currency || 'USD', data.value) : 0 }}
            </template>
            <template #cell(actions)="{ item }">
              <!-- Если источник НЕ заблокирован - показываем кнопку "Блок" -->
              <b-button
                v-if="!isSourceBlocked(item.site_id)"
                v-ripple.400="'rgba(220, 53, 69, 0.15)'"
                variant="outline-danger"
                size="sm"
                @click="showBlockModal(item)"
              >
                {{ $t('stats.block') }}
              </b-button>
              <!-- Если источник заблокирован - показываем кнопки "Амнистия" и "Разблокировать" -->
              <template v-else>
                <b-button
                  v-ripple.400="'rgba(255, 193, 7, 0.15)'"
                  variant="outline-warning"
                  size="sm"
                  class="mr-1"
                  @click="showAmnestyModalFromActive(item)"
                >
                  {{ $t('stats.amnesty') }}
                </b-button>
                <b-button
                  v-ripple.400="'rgba(40, 167, 69, 0.15)'"
                  variant="outline-success"
                  size="sm"
                  @click="unblockSourceFromActive(item)"
                >
                  {{ $t('stats.unblock') }}
                </b-button>
              </template>
            </template>
            <template #foot(site_id)="data">
              <strong :data-label="data.label">{{ $t('Records') }}: {{ siteTotalRecords }}</strong>
            </template>
            <template #foot(site_id_raw)>
              <span />
            </template>
            <template #foot(impressions)="data">
              <strong :data-label="data.label">{{ totalFormat(statsBySite, 'impressions').toLocaleString('ru-RU') }}</strong>
            </template>
            <template #foot(clicks)="data">
              <strong :data-label="data.label">{{ new Intl.NumberFormat('en-GB', { style: 'decimal' }).format(totalFormat(statsBySite, 'clicks')) }}</strong>
            </template>
            <template #foot(ctr)="data">
              <strong :data-label="data.label">
                {{ totalFormat(statsBySite, 'clicks') ? new Intl.NumberFormat('en-GB', { style: 'decimal', maximumFractionDigits: 3 }).format(totalFormat(statsBySite, 'clicks') / totalFormat(statsBySite, 'impressions') * 100) : 0 }}%
              </strong>
            </template>
            <template #foot(revenue)="data">
              <strong :data-label="data.label">{{ currency('USD', totalAmount(statsBySite)) }}</strong>
            </template>
            <template #foot(spent)="data">
              <strong :data-label="data.label">{{ currency(campaign.currency || 'USD', totalFormat(statsBySite, 'spent')) }}</strong>
            </template>
            <template #foot(actions)>
              <span />
            </template>
          </b-table>

          <!-- Pagination for statsBySite -->
          <div
            v-if="statsBySite.length"
            class="d-flex justify-content-between align-items-center mt-2"
          >
            <div class="d-flex align-items-center">
              <span class="mr-2">{{ $t('message.pagelength') }}:</span>
              <b-form-select
                v-model="siteLimit"
                :options="siteLimitOptions"
                size="sm"
                class="w-auto"
                @change="onSiteLimitChange"
              />
              <span class="ml-2"> {{ $t('message.of') }} {{ siteTotalRecords }} {{ $t('message.pageText2') }}</span>
            </div>
            <b-pagination
              v-model="siteCurrentPage"
              :total-rows="siteTotalRecords"
              :per-page="siteLimit"
              :limit="5"
              align="right"
              size="md"
              @change="onSitePageChange"
            />
          </div>
        </b-tab>
        <b-tab :title="$t('stats.blocked_sources')">
          <div
            v-if="isLoadingBlocked"
            class="text-center my-3"
          >
            <b-spinner label="Loading..." />
          </div>
          <div
            v-else-if="!Array.isArray(blockedSources) || !blockedSources.length"
            class="text-center my-3"
          >
            <p>{{ $t('stats.no_blocked_sources') }}</p>
          </div>
          <b-table
            v-else
            :items="blockedSources"
            :fields="blockedSourcesFields"
            striped
            responsive
            small
            show-empty
            class="text-center"
          >
            <template #head(name)>
              {{ $t('stats.source_name') }}
            </template>
            <template #head(site_id)>
              ID
            </template>
            <template #head(reason_with_date)>
              {{ $t('stats.block_reason') }}
            </template>
            <template #head(actions)>
              {{ $t('stats.actions') }}
            </template>
            <template #cell(name)="{ item }">
              <div class="d-flex align-items-center justify-content-center position-relative">
                {{ getDomainNameById(item.site_id) || item.site_id }}
                <feather-icon
                  v-if="getDomainNameById(item.site_id)"
                  icon="ExternalLinkIcon"
                  size="14"
                  class="ml-25 cursor-pointer text-primary"
                  @click.stop="openWebsite(getDomainNameById(item.site_id))"
                />
              </div>
            </template>
            <template #cell(site_id)="{ item }">
              <span
                class="cursor-pointer copy-id"
                @click="copyToClipboard(item.site_id)"
              >
                {{ item.site_id }}
              </span>
            </template>
            <template #cell(reason_with_date)="{ item }">
              <div>
                <div class="font-weight-medium">
                  {{ item.reason || $t('stats.blocked_by_admin') }}
                </div>
                <small
                  v-if="item.block_date"
                  class="text-muted"
                >
                  {{ $t('stats.blocked_on') }}: {{ getFormattedDate(new Date(item.block_date)) }}
                </small>
                <small
                  v-else
                  class="text-muted"
                >
                  {{ $t('stats.blocked_on') }}: {{ getFormattedDate(new Date(item.created)) }}
                </small>
              </div>
            </template>
            <template #cell(actions)="{ item }">
              <b-button
                v-ripple.400="'rgba(255, 193, 7, 0.15)'"
                variant="outline-warning"
                size="sm"
                class="mr-1"
                @click="showAmnestyModal(item)"
              >
                {{ $t('stats.amnesty') }}
              </b-button>
              <b-button
                v-ripple.400="'rgba(40, 167, 69, 0.15)'"
                variant="outline-success"
                size="sm"
                @click="unblockSource(item)"
              >
                {{ $t('stats.unblock') }}
              </b-button>
            </template>
          </b-table>
        </b-tab>
      </b-tabs>
    </b-card>

    <!-- Block Source Modal -->
    <b-modal
      v-model="showBlockSourceModal"
      :title="$t('stats.block_source')"
      @hidden="resetBlockForm"
      @ok="blockSource"
    >
      <div class="mb-3">
        <label class="form-label">{{ $t('stats.source') }}:</label>
        <p class="font-weight-bold">
          {{ selectedSource ? getDomainNameById(selectedSource.site_id) : '' }}
        </p>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ $t('stats.block_duration') }}:</label>
        <b-button-group class="w-100 mb-2">
          <b-button
            v-for="option in blockDurationOptions"
            :key="option.value"
            size="sm"
            :variant="blockForm.durationType === option.value ? 'primary' : 'outline-primary'"
            @click="blockForm.durationType = option.value"
          >
            {{ option.label }}
          </b-button>
        </b-button-group>

        <div
          v-if="blockForm.durationType === 'custom'"
        >
          <flat-pickr
            v-model="blockForm.customDate"
            :config="blockDatePickerConfig"
            class="form-control"
            :placeholder="$t('stats.select_date')"
          />
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ $t('stats.block_reason') }}:</label>
        <b-form-textarea
          v-model="blockForm.reason"
          :placeholder="$t('stats.block_reason_placeholder')"
          rows="3"
        />
      </div>
    </b-modal>

    <!-- Amnesty Modal -->
    <b-modal
      v-model="showAmnestySourceModal"
      :title="$t('stats.edit_block_info')"
      @hidden="resetAmnestyForm"
      @ok="updateBlockInfo"
    >
      <div class="mb-3">
        <label class="form-label">{{ $t('stats.source') }}:</label>
        <p class="font-weight-bold">
          {{ selectedBlockedSource ? getDomainNameById(selectedBlockedSource.site_id) : '' }}
        </p>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ $t('stats.block_duration') }}:</label>
        <b-button-group class="w-100 mb-2">
          <b-button
            v-for="option in blockDurationOptions"
            :key="option.value"
            size="sm"
            :variant="amnestyForm.durationType === option.value ? 'primary' : 'outline-primary'"
            @click="amnestyForm.durationType = option.value"
          >
            {{ option.label }}
          </b-button>
        </b-button-group>

        <div
          v-if="amnestyForm.durationType === 'custom'"
        >
          <flat-pickr
            v-model="amnestyForm.customDate"
            :config="blockDatePickerConfig"
            class="form-control"
            :placeholder="$t('stats.select_date')"
          />
        </div>
      </div>

      <div class="mb-3">
        <label class="form-label">{{ $t('stats.block_reason') }}:</label>
        <b-form-textarea
          v-model="amnestyForm.reason"
          :placeholder="$t('stats.block_reason_placeholder')"
          rows="3"
        />
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard, BTable, BTabs, BTab, BSpinner, BButton, BPagination, BFormSelect,
  BModal, BButtonGroup, BFormTextarea, VBTooltip,
} from 'bootstrap-vue'
import FeatherIcon from '@/@core/components/feather-icon/FeatherIcon.vue'
import Breadcrumbs from '@/views/ui/breadcrumbs/Breadcrumbs.vue'
import UserToolbar from '@/views/dashboard/users/UserToolbar.vue'
import CampaignToolbar from '@/views/dashboard/campaigns/CampaignToolbar.vue'
import CampaignNavigation from '@/views/dashboard/campaigns/CampaignNavigation.vue'
import useJwt from '@/auth/jwt/useJwt'
import currencyFormatter from '@/utils/currency-formatter'
import getFormattedDate from '@/utils/date-formatter'
import StatsDateRangeFilter from '@/views/dashboard/dsppartners/stats/StatsDateRangeFilter.vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import flatPickr from 'vue-flatpickr-component'
import Ripple from 'vue-ripple-directive'
import { mapGetters } from 'vuex'

export default {
  name: 'RtbStatsPartner',
  components: {
    Breadcrumbs,
    UserToolbar,
    CampaignToolbar,
    CampaignNavigation,
    BCard,
    BTable,
    BTabs,
    BTab,
    BSpinner,
    BButton,
    BPagination,
    BFormSelect,
    BModal,
    BButtonGroup,
    BFormTextarea,
    flatPickr,
    FeatherIcon,
    StatsDateRangeFilter,
  },
  directives: {
    'b-tooltip': VBTooltip,
    Ripple,
  },
  data() {
    return {
      currency: currencyFormatter,
      getFormattedDate,
      breadcrumbItems: [],
      account: {},
      campaign: {},
      partnerNames: {},
      rangePicker: [
        getFormattedDate(new Date(new Date().getFullYear(), new Date().getMonth(), 1)),
        getFormattedDate(new Date()),
      ],
      activeTab: 0,
      statsByDate: [],
      statsBySite: [],
      dateColumns: [
        { key: 'date', label: 'Дата', tdClass: 'td-width' },
        { key: 'impressions', label: 'Impressions', tdClass: 'td-width' },
        { key: 'clicks', label: 'Clicks', tdClass: 'td-width' },
        { key: 'ctr', label: 'CTR', tdClass: 'td-width' },
        { key: 'revenue', label: 'Revenue', tdClass: 'td-width' },
        { key: 'spent', label: 'Spent', tdClass: 'td-width' },
      ],
      siteColumns: [
        {
          key: 'site_id',
          label: 'Site',
          sortable: false,
        },
        {
          key: 'site_id_raw',
          label: 'ID',
          sortable: false,
        },
        {
          key: 'impressions',
          label: 'Impressions',
          sortable: true,
        },
        {
          key: 'clicks',
          label: 'Clicks',
          sortable: true,
        },
        {
          key: 'ctr',
          label: 'CTR',
          sortable: false,
        },
        {
          key: 'revenue',
          label: 'Revenue',
          sortable: true,
        },
        {
          key: 'spent',
          label: 'Spent',
          sortable: false,
        },
        {
          key: 'actions',
          label: 'Actions',
          sortable: false,
        },
      ],
      loading: true,
      // Pagination for statsBySite
      siteLimit: 20,
      siteCurrentPage: 1,
      siteTotalRecords: 0,
      siteLimitOptions: [
        { value: 10, text: '10' },
        { value: 20, text: '20' },
        { value: 50, text: '50' },
        { value: 100, text: '100' },
      ],
      // Sorting for statsBySite
      siteSortBy: 'impressions',
      siteSortDesc: true,
      siteSortDirection: 'desc',
      // Blocked sources
      blockedSources: [],
      isLoadingBlocked: false,
      // Modals
      showBlockSourceModal: false,
      showAmnestySourceModal: false,
      selectedSource: null,
      selectedBlockedSource: null,
      // Block form
      blockForm: {
        durationType: 'today',
        customDate: '',
        reason: '',
      },
      // Amnesty form
      amnestyForm: {
        durationType: 'today',
        customDate: '',
        reason: '',
      },
      // Block duration options
      blockDurationOptions: [
        { value: 'today', label: 'Сегодня' },
        { value: 'tomorrow', label: 'Завтра' },
        { value: 'custom', label: 'Выбрать дату' },
      ],
      // Date picker config
      blockDatePickerConfig: {
        dateFormat: 'Y-m-d',
        altInput: true,
        altFormat: 'Y-m-d',
        minDate: 'today',
      },
      // Blocked sources fields
      blockedSourcesFields: [
        { key: 'name', label: 'Источник' },
        { key: 'site_id', label: 'ID' },
        { key: 'reason_with_date', label: 'Причина блокировки', thClass: 'td-width' },
        { key: 'actions', label: 'Действия' },
      ],
    }
  },
  computed: {
    ...mapGetters('dashboard-campaings-stats', ['campaignNameByIdFromStore']),
    getDomainNameById() {
      return siteId => {
        const domainName = this.$store.getters['dashboard-dsp-domains/domainNameByIdFromStore'](siteId)

        return domainName
      }
    },
    siteOffset() {
      return (this.siteCurrentPage - 1) * this.siteLimit
    },
  },
  created() {
    this.getUser()
    this.getCampaignData()

    // Priority: URL params > saved settings > default values
    const { from, to } = this.$route.query
    if (from && to) {
      // If URL has params, use them
      this.rangePicker = [from, to]
    } else {
      // Otherwise try to load saved settings
      this.loadSavedSettings()
    }

    // Load blocked sources for proper isSourceBlocked() functionality
    this.loadBlockedSources()
  },
  methods: {
    async getUser() {
      const responseData = await useJwt.getUser(this.$route.params.userId)
      this.account = responseData.data.account || {}
    },
    async getCampaignData() {
      const responseData = await useJwt.getCampaign(this.$route.params.id)
      this.campaign = responseData.data.campaign || {}
      this.setBreabcrumbs()
      this.getStatsData()
    },
    async getStatsData() {
      this.loading = true
      try {
        await Promise.all([
          this.getStatsByDate(),
          this.getStatsBySite(),
          this.loadPartnerNames(),
        ])
      } catch (error) {
        this.statsByDate = []
        this.statsBySite = []
      } finally {
        this.loading = false
      }
    },
    async getStatsByDate() {
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.statsByDate = []
        return
      }

      const params = {
        partner_id: this.$route.params.partnerId,
        campaign_id: this.$route.params.id,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
      }
      const responseData = await useJwt.getPhoenixDspPartner(params)
      this.statsByDate = responseData.data.items || []
    },
    async getStatsBySite() {
      if (!this.rangePicker || this.rangePicker.length < 2) {
        this.statsBySite = []
        this.siteTotalRecords = 0
        return
      }

      const params = {
        campaign_id: this.$route.params.id,
        partner_id: this.$route.params.partnerId,
        from: this.rangePicker[0],
        to: this.rangePicker[1],
        type: 'daily',
        limit: this.siteLimit,
        offset: this.siteOffset,
        sort_by: this.siteSortBy,
        sort_desc: this.siteSortDesc,
      }
      const responseData = await useJwt.getPhoenixDspSiteTotal(params)
      this.statsBySite = responseData.data.items || []
      this.siteTotalRecords = responseData.data.total || this.statsBySite.length

      // Load domain names for sites
      const siteIds = [...new Set(this.statsBySite.map(item => item.site_id || item.site).filter(Boolean))]

      if (siteIds.length) {
        try {
          await this.$store.dispatch('dashboard-dsp-domains/fetchDomainNamesById', {
            dspId: this.$route.params.partnerId,
            siteIds,
          })
        } catch (error) {
          // Error loading domain names
        }
      }
    },
    toggleRange(selectedDates) {
      if (!selectedDates || !Array.isArray(selectedDates) || selectedDates.length !== 2) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Range Error',
            msg: 'Invalid date range format',
            variant: 'danger',
          },
          { root: true },
        )
        return
      }

      const [fromDate, toDate] = selectedDates
      const from = getFormattedDate(new Date(fromDate))
      const to = getFormattedDate(new Date(toDate))

      // Only update if we have valid dates
      if (from !== 'Invalid date' && to !== 'Invalid date') {
        // Check if dates actually changed
        if (this.rangePicker[0] === from && this.rangePicker[1] === to) {
          return
        }

        // Update state and URL in one go
        this.rangePicker = [from, to]
        this.siteCurrentPage = 1 // Reset to first page when changing date range
        // Update URL without page reload
        const newQuery = { ...this.$route.query, from, to }
        const newPath = this.$route.path
        window.history.replaceState(
          {},
          '',
          `${newPath}?${new URLSearchParams(newQuery).toString()}`,
        )
        this.getStatsData()
        this.saveSettings()
      } else {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Date Format Error',
            msg: 'Invalid date format',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    refreshData() {
      this.getStatsData()
    },
    saveSettings() {
      try {
        localStorage.setItem('rtbStatsPartnerDateFilter', JSON.stringify({ rangePicker: this.rangePicker }))
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to save date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    loadSavedSettings() {
      try {
        const saved = localStorage.getItem('rtbStatsPartnerDateFilter')
        if (saved) {
          const settings = JSON.parse(saved)
          if (settings.rangePicker && !this.$route.query.from && !this.$route.query.to) {
            this.rangePicker = settings.rangePicker
          }
        }
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Settings Error',
            msg: 'Failed to load date filter settings',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async loadPartnerNames() {
      try {
        const responseData = await useJwt.dsppartners()
        const partners = responseData.data.dsppartners || []
        this.partnerNames = {}
        partners.forEach(partner => {
          this.partnerNames[partner._id] = partner.name
        })
      } catch (error) {
        // Error loading partner names
      }
    },
    getPartnerName(partnerId) {
      return this.partnerNames[partnerId] || null
    },
    setBreabcrumbs() {
      this.breadcrumbItems = [
        {
          text: this.$t('Users'),
          to: '/users/',
        },
        {
          text: this.account.name,
          to: `/users/${this.$route.params.userId}/`,
        },
        {
          text: this.$t('Campaigns'),
          to: `/users/${this.$route.params.userId}/campaigns`,
        },
        {
          text: this.campaign.title,
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}`,
        },
        {
          text: 'DSP Sources',
          to: `/users/${this.$route.params.userId}/campaigns/${this.$route.params.id}/rtb-stats/sources`,
        },
        {
          text: this.getPartnerName(this.$route.params.partnerId) || this.$route.params.partnerId,
          active: true,
        },
      ]
    },
    totalFormat(arr, field) {
      let total = 0
      arr.forEach(i => {
        if (field === 'site') return // Skip site field for totals
        total += i[field] || 0
      })
      return total
    },
    totalAmount(arr) {
      let total = 0
      arr.forEach(i => {
        total += i.revenue || 0
      })
      return total
    },
    goToSources() {
      this.$router.push({ name: 'campaign-rtb-stats-sources', params: { userId: this.$route.params.userId, id: this.$route.params.id } })
    },
    goToSourceDetails(siteId) {
      // Navigate to source details page
      this.$router.push({
        name: 'campaign-rtb-stats-source',
        params: {
          userId: this.$route.params.userId,
          id: this.$route.params.id,
          partnerId: this.$route.params.partnerId,
          sourceId: siteId,
        },
      })
    },
    openWebsite(domainName) {
      if (!domainName) return

      // Add protocol if missing
      let url = domainName
      if (!url.startsWith('http://') && !url.startsWith('https://')) {
        url = `https://${url}`
      }

      // Open in new window
      window.open(url, '_blank', 'noopener,noreferrer')
    },
    onSitePageChange(page) {
      this.siteCurrentPage = page
      this.getStatsBySite()
    },
    onSiteLimitChange() {
      this.siteCurrentPage = 1 // Reset to first page when changing limit
      this.getStatsBySite()
    },
    onSiteSortChanged(ctx) {
      // Update sorting only for fields supported by backend
      const backendSortableFields = ['impressions', 'clicks', 'revenue']
      if (backendSortableFields.includes(ctx.sortBy)) {
        this.siteSortBy = ctx.sortBy
        this.siteSortDesc = ctx.sortDesc
        this.siteCurrentPage = 1 // Reset to first page when changing sort
        this.getStatsBySite()
      }
    },
    // Tab methods
    onTabChange(tabIndex) {
      this.activeTab = tabIndex
      if (tabIndex === 2) {
        this.loadBlockedSources()
      } else if (tabIndex === 1) {
        // Update blocked sources for correct button functionality in main list
        this.loadBlockedSources()
      }
    },
    // Blocked sources methods
    async loadBlockedSources() {
      this.isLoadingBlocked = true
      try {
        const response = await useJwt.getCampaigndspBlacklist({
          campaign_id: this.$route.params.id,
          dsp_partner_id: this.$route.params.partnerId,
        })

        // Try different response structure variants
        let dataArray = response.data
        if (response.data && response.data.data) {
          dataArray = response.data.data
        } else if (response.data && response.data.campaigndspblacklist) {
          dataArray = response.data.campaigndspblacklist
        }

        this.blockedSources = Array.isArray(dataArray) ? dataArray : []

        // Load domain names for blocked sources
        const siteIds = [...new Set((this.blockedSources || []).map(item => item.site_id).filter(Boolean))]
        if (siteIds.length) {
          try {
            await this.$store.dispatch('dashboard-dsp-domains/fetchDomainNamesById', {
              dspId: this.$route.params.partnerId,
              siteIds,
            })
          } catch (error) {
            // Error loading domain names for blocked sources
          }
        }
      } catch (error) {
        this.blockedSources = []
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to load blocked sources',
            variant: 'danger',
          },
          { root: true },
        )
      } finally {
        this.isLoadingBlocked = false
      }
    },
    // Modal methods
    showBlockModal(item) {
      this.selectedSource = item
      this.resetBlockForm()
      this.showBlockSourceModal = true
    },
    showAmnestyModal(item) {
      this.selectedBlockedSource = item
      this.resetAmnestyForm()
      // Pre-fill form with current values
      this.amnestyForm.reason = item.reason || ''
      if (item.block_date) {
        this.amnestyForm.durationType = 'custom'
        this.amnestyForm.customDate = getFormattedDate(new Date(item.block_date))
      }
      this.showAmnestySourceModal = true
    },
    async showAmnestyModalFromActive(item) {
      // For amnesty from main list, need to find blocking record
      try {
        const blacklistResponse = await useJwt.getCampaigndspBlacklist({
          campaign_id: this.$route.params.id,
          dsp_partner_id: this.$route.params.partnerId,
        })

        // Use the same response processing logic as in loadBlockedSources
        let blacklistData = blacklistResponse.data
        if (blacklistResponse.data && blacklistResponse.data.data) {
          blacklistData = blacklistResponse.data.data
        } else if (blacklistResponse.data && blacklistResponse.data.campaigndspblacklist) {
          blacklistData = blacklistResponse.data.campaigndspblacklist
        }

        // Make sure it's an array
        const blacklistArray = Array.isArray(blacklistData) ? blacklistData : []
        const blockItem = blacklistArray.find(blockRecord => blockRecord.site_id === item.site_id)

        if (!blockItem) {
          this.$store.dispatch(
            'error-store/showNotify',
            {
              title: 'Ошибка',
              msg: 'Запись блокировки не найдена',
              variant: 'warning',
            },
            { root: true },
          )
          return
        }

        // Call the regular method with found blocking record
        this.showAmnestyModal(blockItem)
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Ошибка',
            msg: `Не удалось загрузить данные блокировки: ${error.response?.data?.message || error.message}`,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    resetBlockForm() {
      this.blockForm = {
        durationType: 'today',
        customDate: '',
        reason: this.$t('stats.blocked_by_admin'),
      }
    },
    resetAmnestyForm() {
      this.amnestyForm = {
        durationType: 'today',
        customDate: '',
        reason: '',
      }
    },
    // Block/Unblock actions
    formatDateForAPI(date) {
      // Format: 2025-08-06T09:05:01.592000Z
      return date.toISOString()
    },
    isSourceBlocked(siteId) {
      // Check if source is blocked
      if (!Array.isArray(this.blockedSources)) {
        return false
      }

      // Check both strict equality and type conversion
      return this.blockedSources.some(item => item && (item.site_id === siteId || String(item.site_id) === String(siteId)))
    },
    async blockSource() {
      if (!this.selectedSource) return

      try {
        let blockDate = null
        if (this.blockForm.durationType === 'tomorrow') {
          const tomorrow = new Date()
          tomorrow.setDate(tomorrow.getDate() + 1)
          blockDate = this.formatDateForAPI(tomorrow)
        } else if (this.blockForm.durationType === 'custom' && this.blockForm.customDate) {
          blockDate = this.formatDateForAPI(new Date(this.blockForm.customDate))
        }

        await useJwt.createCampaigndspBlacklist({
          campaign_id: this.$route.params.id,
          dsp_partner_id: this.$route.params.partnerId,
          site_id: this.selectedSource.site_id,
          reason: this.blockForm.reason,
          block_date: blockDate,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Source blocked successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Close modal and reset form
        this.showBlockSourceModal = false
        this.resetBlockForm()
        // Always refresh blocked sources to show the new blocked item
        this.loadBlockedSources()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to block source',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async updateBlockInfo() {
      if (!this.selectedBlockedSource) return

      try {
        let blockDate = null
        if (this.amnestyForm.durationType === 'tomorrow') {
          const tomorrow = new Date()
          tomorrow.setDate(tomorrow.getDate() + 1)
          blockDate = this.formatDateForAPI(tomorrow)
        } else if (this.amnestyForm.durationType === 'custom' && this.amnestyForm.customDate) {
          blockDate = this.formatDateForAPI(new Date(this.amnestyForm.customDate))
        }

        await useJwt.updateCampaigndspBlacklist(this.selectedBlockedSource._id, {
          ...this.selectedBlockedSource,
          campaign_id: this.$route.params.id,
          dsp_partner_id: this.$route.params.partnerId,
          reason: this.amnestyForm.reason,
          block_date: blockDate,
        })

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Block information updated successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Refresh blocked sources
        this.loadBlockedSources()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to update block information',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async unblockSource(item) {
      try {
        await useJwt.deleteCampaigndspBlacklist(item._id)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'Source unblocked successfully',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })

        // Refresh both blocked sources and active sources
        this.loadBlockedSources()
        this.getStatsBySite()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: error.response?.statusText || 'Error',
            msg: error.message || 'Failed to unblock source',
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async unblockSourceFromActive(item) {
      // Method for unblocking from active tab
      try {
        // Find blocking record by site_id
        const blacklistResponse = await useJwt.getCampaigndspBlacklist({
          campaign_id: this.$route.params.id,
          dsp_partner_id: this.$route.params.partnerId,
        })

        // Use the same response processing logic as in loadBlockedSources
        let blacklistData = blacklistResponse.data
        if (blacklistResponse.data && blacklistResponse.data.data) {
          blacklistData = blacklistResponse.data.data
        } else if (blacklistResponse.data && blacklistResponse.data.campaigndspblacklist) {
          blacklistData = blacklistResponse.data.campaigndspblacklist
        }

        // Make sure it's an array
        const blacklistArray = Array.isArray(blacklistData) ? blacklistData : []
        const blockItemFound = blacklistArray.find(blockRecord => blockRecord.site_id === item.site_id)

        if (!blockItemFound) {
          this.$store.dispatch(
            'error-store/showNotify',
            {
              title: 'Ошибка',
              msg: 'Запись блокировки не найдена',
              variant: 'warning',
            },
            { root: true },
          )
          return
        }

        // Delete blocking
        await useJwt.deleteCampaigndspBlacklist(blockItemFound._id)

        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Успех',
            msg: 'Источник успешно разблокирован',
            variant: 'success',
          },
          { root: true },
        )

        // Update blocked sources list
        await this.loadBlockedSources()
        this.getStatsBySite()
      } catch (error) {
        this.$store.dispatch(
          'error-store/showNotify',
          {
            title: 'Ошибка',
            msg: `Не удалось разблокировать источник: ${error.response?.data?.message || error.message}`,
            variant: 'danger',
          },
          { root: true },
        )
      }
    },
    async copyToClipboard(text) {
      try {
        await navigator.clipboard.writeText(text)
        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      } catch (error) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea')
        textArea.value = text
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand('copy')
        document.body.removeChild(textArea)

        this.$toast({
          component: ToastificationContent,
          props: {
            title: 'ID copied to clipboard',
            icon: 'CheckIcon',
            variant: 'success',
          },
        })
      }
    },

  },
}
</script>

<style scoped lang="scss">
.card-statistics .card-body {
  min-height: 190px;
}

.card-statistics-tools {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

h4 {
  a {
    color: inherit;
    text-decoration: none;
    &:hover {
      color: #776bf0;
    }
  }
}

@media (max-width: 440px) {
  [dir] #app {
    .card-statistics-tools {
      flex-wrap: wrap;
    }
  }
}

.ml-25 {
  margin-left: 0.25rem;
}

.cursor-pointer {
  cursor: pointer;
}

.text-primary {
  color: #007bff !important;
}

.copy-id {
  border-bottom: 1px dashed #ccc;
  transition: border-bottom-color 0.2s ease;
}

.copy-id:hover {
  border-bottom-color: #007bff;
}
</style>
<style lang="scss">
@import '@core/scss/vue/libs/vue-flatpicker.scss';
</style>
<style>
[dir] .table-responsive .table-responsive {
  margin-bottom: 0!important;
}

[dir] .table.b-table > tbody td.td-width {
  width: 14%;
}
</style>
